import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from 'components/loader/LoadingSpinner';
import Button from 'components/utils/Button';
import Input from 'components/utils/Input';
import { AvoSlideCloseModal } from 'components/utils/panels/AvoSlideCloseModal';
import RequiredMark from 'components/utils/requiredMark';
import { Body2, Caption2, H4 } from 'components/utils/typo';
import { X } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ComboBox } from '../../utils/ComboBox';
import { ModuleContext } from '../../utils/module/ModuleContext';
import {
  TeamCustomizableKnowledgeBase,
  teamCustomizableKnowledgeBaseApi,
} from 'api/resources/team-customizable-knowledge-base';
import clsx from 'clsx';
import { DefaultModuleSuggestions } from '../../../hooks/useSuggestions';
import { mentionAPI } from '../../../api/mention';
import { useTeam } from '../../../hooks/useTeam';
import { useTeamKnowledgeBases } from '../../../hooks/module/useTeamKnowledgeBases';

export const TeamCustomizableKnowledgeBaseForm = ({
  resource,
  onCancel,
}: {
  resource?: TeamCustomizableKnowledgeBase;
  onCancel: () => void;
}) => {
  const { team } = useTeam();
  const { module } = useContext(ModuleContext);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const defaultValues: Omit<TeamCustomizableKnowledgeBase, 'last_updated' | 'unique_code'> = {
    id: resource?.id,
    title: resource?.title ?? '',
    description: resource?.description ?? '',
    default_knowledge_base: resource?.default_knowledge_base,
    module: module?.id,
  };

  const { refetch } = useTeamKnowledgeBases();
  const { data } = useQuery({
    queryKey: ['team', team?.id, 'resources'],
    queryFn: mentionAPI.getModulesSuggestions,
    select: (data) => data.knowledge_base,
    initialData: DefaultModuleSuggestions,
  });

  const comboBoxOptions = useMemo(
    () =>
      data?.map((tkb) => ({
        label: tkb.title || '',
        value: String(tkb.id),
      })) || [],
    [data]
  );

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    setError,
    getValues,
    formState: { errors, isDirty },
  } = useForm<TeamCustomizableKnowledgeBase>({
    defaultValues,
  });

  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: resource?.id
      ? teamCustomizableKnowledgeBaseApi.put
      : teamCustomizableKnowledgeBaseApi.post,
  });

  const onInvalid = (errors) => {
    const data = getValues();
    if (!data.default_knowledge_base) {
      setError('default_knowledge_base', {
        type: 'required',
        message: 'Knowledge Base is required',
      });
      return;
    }
  };

  const onSubmit = async (data: TeamCustomizableKnowledgeBase) => {
    if (isPending) return;

    await mutateAsync(data);
    refetch();
    onCancel();
  };

  return (
    <>
      <form className='flex h-full flex-col' onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <div className='grow overflow-y-auto'>
          <div className='sticky top-0 z-10 flex items-center justify-between bg-white pl-4 pr-3 pt-[20px]'>
            <H4>Team-Customizable Knowledge Base</H4>
            <div
              className='cursor-pointer'
              onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
            >
              <X size={20} />
            </div>
          </div>
          <div className='mt-4 px-4'>
            <div className='space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Title</Body2>
                <RequiredMark />
              </div>
              <Input
                tabIndex={1}
                {...register('title', {
                  required: { value: true, message: 'Title field is required' },
                  maxLength: 200,
                })}
                maxLength={200}
                className={clsx(
                  errors.title &&
                    'border-red-500 ring-1 !ring-red-500' /* TODO: Need to remove ring after remove global style*/
                )}
              />
              {errors.title && <span className='text-error'>{errors.title.message}</span>}
              <Caption2 className='flex justify-end text-gray-text-weak'>
                {watch('title').length}/200
              </Caption2>
            </div>
            <div className='mt-1 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Description</Body2>
                <RequiredMark />
              </div>
              <Input
                tabIndex={2}
                {...register('description', {
                  maxLength: 1000,
                })}
                maxLength={1000}
              />
              <Caption2 className='flex justify-end text-gray-text-weak'>
                {watch('description').length}/1000
              </Caption2>
            </div>
            <div className='mt-6 space-y-1'>
              <div className='flex gap-1'>
                <Body2 className='text-gray-900'>Default Knowledge Base</Body2>
                <RequiredMark />
              </div>
              <div
                className={clsx(
                  'rounded',
                  errors.default_knowledge_base &&
                    'border-red-500 ring-1 !ring-red-500' /* TODO: Need to remove ring after remove global style*/
                )}
              >
                <ComboBox
                  onChange={(value) =>
                    setValue('default_knowledge_base', Number(value), { shouldDirty: true })
                  }
                  options={comboBoxOptions}
                  selectedValue={String(watch('default_knowledge_base'))}
                />
              </div>
              {errors.default_knowledge_base && (
                <span className='text-error'>{errors.default_knowledge_base?.message}</span>
              )}
            </div>
          </div>
        </div>
        <div className='shrink-0'>
          <div className='flex justify-end gap-2 px-[12px] pb-[22px] pt-[12px]'>
            <div className='flex'>
              <Button.Reverse
                type='button'
                onClick={() => (isDirty ? setCloseModalOpen(true) : onCancel())}
                disabled={isPending}
              >
                Cancel
              </Button.Reverse>
            </div>
            <div className='flex w-[80px]'>
              <Button disabled={isPending}>{isPending ? <LoadingSpinner /> : 'Save'}</Button>
            </div>
          </div>
        </div>
      </form>
      <AvoSlideCloseModal
        open={closeModalOpen}
        onContinue={() => setCloseModalOpen(false)}
        onDiscard={onCancel}
      />
    </>
  );
};
